/**
 * Policies (like PowerDMS): search every policy, read it, sign that you read it. Policy managers write and
 * publish versions, choose who has to sign, see who hasn't, and import the old system's signatures.
 */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, day, EmptyState, fileBase64, PeoplePicker, when } from './common.tsx';

const MINE: Record<string, { label: string; tone: 'ok' | 'warn' | 'bad' | 'neutral' }> = {
  signed: { label: 'Signed', tone: 'ok' },
  to_sign: { label: 'To sign', tone: 'warn' },
  overdue: { label: 'Overdue', tone: 'bad' },
  not_required: { label: '—', tone: 'neutral' },
};
const pname = (p: any) => `${p.number ? `${p.number} · ` : ''}${p.title}`;

export function PoliciesPage() {
  const { navigate } = useRouter();
  const qs = new URLSearchParams(location.search);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [tab, setTab] = useState(qs.get('mine') === 'to-sign' ? 'mine' : 'all');
  const status = tab === 'drafts' ? 'draft' : tab === 'archived' ? 'archived' : 'published';
  const r = useLoad(() => api.get<any>(`/api/policies?q=${encodeURIComponent(q)}&category=${encodeURIComponent(category)}&status=${status}${tab === 'mine' ? '&mine=to-sign' : ''}`), [q, category, tab]);
  const tabs: [string, string][] = [
    ['all', 'All policies'],
    ['mine', 'To read & sign'],
    ...(r.data?.canManage ? ([['drafts', 'Drafts'], ['archived', 'Archived']] as [string, string][]) : []),
  ];
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Resources"
        title="Policies"
        sub="Search every policy. When one is assigned to you, open it, read it, and sign that you have."
        actions={
          r.data?.canManage && (
            <div className="row gap-sm">
              <button className="btn" onClick={() => navigate(at('/policies/import'))}>
                Import signatures
              </button>
              <button className="btn primary" onClick={() => navigate(at('/policies/new'))}>
                New policy
              </button>
            </div>
          )
        }
      />
      <input type="search" className="pol-search" placeholder="Search policies — title, number, or any words in the policy" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search policies" />
      <div className="row gap-sm wrap">
        <div className="seg-ctl" role="tablist" aria-label="Which policies">
          {tabs.map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>
              {l}
            </button>
          ))}
        </div>
        {(r.data?.categories ?? []).length > 0 && (
          <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category" style={{ maxWidth: 220 }}>
            <option value="">All categories</option>
            {r.data.categories.map((c: string) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        )}
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.policies.length ? (
        <EmptyState icon="book" title={tab === 'mine' ? 'Nothing to sign — you’re caught up' : q ? 'No policy matches that' : 'No policies yet'} />
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Policy</th>
                <th>Category</th>
                <th>Effective</th>
                <th>You</th>
                {r.data.canManage && <th>Signed</th>}
              </tr>
            </thead>
            <tbody>
              {r.data.policies.map((p: any) => (
                <tr key={p.id} className="clickable" onClick={() => navigate(at(`/policies/${p.id}`))}>
                  <td>
                    <strong>{pname(p)}</strong>
                    {p.version ? <span className="small muted"> · v{p.version}</span> : null}
                    {p.snippet && <div className="small muted pol-snip">{p.snippet}</div>}
                  </td>
                  <td>{p.category ?? '—'}</td>
                  <td>{p.effective_on ? day(p.effective_on) : '—'}</td>
                  <td>
                    {p.mine !== 'not_required' ? (
                      <Badge tone={MINE[p.mine]!.tone} dot>
                        {MINE[p.mine]!.label}
                      </Badge>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  {r.data.canManage && (
                    <td>
                      {p.assigned ? (
                        <>
                          <div className="ap-bar">
                            <span style={{ width: `${(p.signed / p.assigned) * 100}%` }} />
                          </div>
                          <span className="small muted">
                            {p.signed} of {p.assigned}
                          </span>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PolicyForm({ init, onSave, busy, submit }: { init: Record<string, any>; onSave: (v: Record<string, any>) => void; busy: boolean; submit: string }) {
  const meta = useLoad(() => api.get<any>('/api/policies/meta'));
  const [f, setF] = useState<Record<string, any>>({ assignAll: true, assignUnits: [], assignPeople: [], dueDays: 14, ...init });
  const set = (k: string, v: unknown) => setF((x) => ({ ...x, [k]: v }));
  if (!meta.data) return meta.error ? <ErrorBox error={meta.error} /> : <Spinner />;
  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ ...f, dueDays: Number(f.dueDays) || 14, reviewOn: f.reviewOn || null });
      }}
    >
      <Card title="The policy">
        <div className="grid-3">
          <label>
            Number
            <input value={f.number ?? ''} onChange={(e) => set('number', e.target.value)} placeholder="300.1" />
          </label>
          <label style={{ gridColumn: 'span 2' }}>
            Title
            <input required value={f.title ?? ''} onChange={(e) => set('title', e.target.value)} placeholder="Vehicle Pursuits" />
          </label>
          <label>
            Category
            <input list="pol-cats" value={f.category ?? ''} onChange={(e) => set('category', e.target.value)} placeholder="Operations" />
            <datalist id="pol-cats">
              {meta.data.categories.map((c: string) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </label>
          <label>
            Days to sign after it’s published
            <input type="number" min="1" max="365" value={f.dueDays} onChange={(e) => set('dueDays', e.target.value)} />
          </label>
          <label>
            Review by
            <input type="date" value={f.reviewOn ?? ''} onChange={(e) => set('reviewOn', e.target.value)} />
          </label>
        </div>
      </Card>
      <Card title="Who has to read and sign it">
        <label className="check">
          <input type="checkbox" checked={f.assignAll} onChange={(e) => set('assignAll', e.target.checked)} /> Everyone
        </label>
        {!f.assignAll && (
          <div className="stack sm">
            <div className="small strong">Divisions, shifts or units</div>
            <div className="bwc-checks">
              {meta.data.units.map((u: any) => (
                <label key={u.id} className="check">
                  <input type="checkbox" checked={f.assignUnits.includes(u.id)} onChange={() => set('assignUnits', f.assignUnits.includes(u.id) ? f.assignUnits.filter((x: string) => x !== u.id) : [...f.assignUnits, u.id])} /> {u.name}
                </label>
              ))}
            </div>
            <PeoplePicker people={meta.data.people} value={f.assignPeople} onChange={(v) => set('assignPeople', v)} label="People" />
          </div>
        )}
      </Card>
      <div>
        <button className="btn primary" disabled={busy}>
          {submit}
        </button>
      </div>
    </form>
  );
}

export function NewPolicy() {
  const { navigate } = useRouter();
  const a = useAction();
  return (
    <div className="ops stack">
      <PageHeader crumb="Policies" title="New policy" sub="Save it, then upload the policy and publish it." />
      <ErrorBox error={a.error} />
      <PolicyForm init={{}} busy={a.busy} submit="Save and add the document" onSave={(v) => a.run(async () => navigate(at(`/policies/${(await api.post<{ id: string }>('/api/policies', v)).id}`), true))} />
    </div>
  );
}

function VersionUpload({ id, onDone }: { id: string; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [eff, setEff] = useState('');
  const [resign, setResign] = useState(true);
  const a = useAction();
  return (
    <div className="stack sm pol-upload">
      <div className="grid-3">
        <label>
          Policy document (PDF)
          <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </label>
        <label>
          Effective date
          <input type="date" value={eff} onChange={(e) => setEff(e.target.value)} />
        </label>
        <label>
          What changed
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="New version for 2026" />
        </label>
      </div>
      {!file && (
        <label>
          …or paste the policy text
          <textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} />
        </label>
      )}
      <label className="check">
        <input type="checkbox" checked={resign} onChange={(e) => setResign(e.target.checked)} /> Everyone has to read and sign this version (uncheck for a typo fix)
      </label>
      <ErrorBox error={a.error} />
      <div>
        <button
          type="button"
          className="btn primary"
          disabled={a.busy || (!file && !text.trim())}
          onClick={() =>
            a.run(async () => {
              await api.post(`/api/policies/${id}/versions`, { fileBase64: file ? await fileBase64(file) : undefined, fileName: file?.name, bodyText: file ? undefined : text, changeNote: note || undefined, effectiveOn: eff || undefined, requiresSign: resign });
              setFile(null);
              setText('');
              setNote('');
              onDone();
            })
          }
        >
          {a.busy ? 'Uploading…' : 'Save as a draft'}
        </button>
      </div>
    </div>
  );
}

export function PolicyDetail({ id }: { id: string }) {
  const { me } = useAuth();
  const r = useLoad(() => api.get<any>(`/api/policies/${id}`), [id]);
  const a = useAction();
  const [read, setRead] = useState(false);
  const [typed, setTyped] = useState('');
  const [editing, setEditing] = useState(false);
  const [showAll, setShowAll] = useState(false);
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { policy: p, current, versions, mine, people, canManage } = r.data;
  const draft = versions.find((v: any) => v.status === 'draft');
  const unsigned = people.filter((x: any) => !x.signed_at);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Policies"
        title={pname(p)}
        sub={[p.category, current && `Version ${current.version}`, current?.effective_on && `effective ${day(current.effective_on)}`].filter(Boolean).join(' · ')}
        actions={
          canManage && (
            <div className="row gap-sm">
              <button className="btn" onClick={() => setEditing(!editing)}>
                {editing ? 'Close' : 'Edit'}
              </button>
              <button className="btn ghost" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/policies/${id}/archive`, { archived: p.status !== 'archived' }), r.reload()))}>
                {p.status === 'archived' ? 'Restore' : 'Archive'}
              </button>
            </div>
          )
        }
      />
      <ErrorBox error={a.error} />
      {p.status === 'draft' && <Notice tone="warn">Not published yet — only policy managers can see it.</Notice>}
      {p.status === 'archived' && <Notice tone="info">Archived — no longer in effect.</Notice>}
      {editing && (
        <PolicyForm
          init={{ number: p.number, title: p.title, category: p.category, assignAll: p.assign_all, assignUnits: p.assign_units, assignPeople: p.assign_people, dueDays: p.due_days, reviewOn: p.review_on ? String(p.review_on).slice(0, 10) : '' }}
          busy={a.busy}
          submit="Save"
          onSave={(v) => a.run(async () => (await api.post(`/api/policies/${id}`, v), setEditing(false), r.reload()))}
        />
      )}
      {mine.mustSign && (
        <Notice tone={mine.due && new Date(mine.due) < new Date() ? 'bad' : 'warn'}>
          You need to read and sign this policy{mine.due ? ` by ${day(mine.due)}` : ''}. Read it below, then sign at the bottom.
        </Notice>
      )}
      {mine.signed && (
        <Notice tone="ok">
          ✓ You signed this {mine.signed.method === 'imported' ? `(imported from ${mine.signed.source ?? 'the old system'})` : ''} on {when(mine.signed.signed_at)}.
        </Notice>
      )}
      {current ? (
        <Card title={`Version ${current.version}${current.change_note ? ` — ${current.change_note}` : ''}`} actions={current.file_name && <a className="btn sm" href={`/api/policies/versions/${current.id}/file`} target="_blank" rel="noreferrer">Open the PDF</a>}>
          {current.file_name ? (
            <iframe className="pol-pdf" src={`/api/policies/versions/${current.id}/file`} title={`${p.title} (PDF)`} />
          ) : current.body_text ? (
            <div className="pol-text pre">{current.body_text}</div>
          ) : (
            <p className="muted">The document hasn’t been uploaded yet.</p>
          )}
        </Card>
      ) : (
        <Card title="Document">
          <p className="muted">No published version yet.</p>
        </Card>
      )}
      {mine.mustSign && (
        <Card title="Sign">
          <div className="stack sm">
            <label className="check">
              <input type="checkbox" checked={read} onChange={(e) => setRead(e.target.checked)} /> I have read and understand this policy, and I will follow it.
            </label>
            <label style={{ maxWidth: 360 }}>
              Type your name
              <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={me?.user.displayName} autoComplete="off" />
            </label>
            <div>
              <button className="btn primary" disabled={a.busy || !read || !typed.trim()} onClick={() => a.run(async () => (await api.post(`/api/policies/${id}/sign`, { typedName: typed }), r.reload()))}>
                Sign
              </button>
            </div>
          </div>
        </Card>
      )}
      {mine.history.length > 1 && (
        <Card title="Your signatures on this policy">
          <ul className="plain small">
            {mine.history.map((h: any, i: number) => (
              <li key={i}>
                {when(h.signed_at)} · {h.version ? `version ${h.version}` : ''} {h.method === 'imported' ? `(imported from ${h.source ?? 'the old system'})` : ''}
              </li>
            ))}
          </ul>
        </Card>
      )}
      {canManage && (
        <>
          <Card title={draft ? `Draft: version ${draft.version}` : 'New version'}>
            {draft && (
              <div className="row gap-sm wrap" style={{ marginBottom: 10 }}>
                <span className="small">
                  {draft.file_name ?? 'Text only'}
                  {draft.change_note ? ` · ${draft.change_note}` : ''} · {draft.requires_sign ? 'everyone signs again' : 'no re-sign'}
                </span>
                {draft.file_name && (
                  <a className="btn sm" href={`/api/policies/versions/${draft.id}/file`} target="_blank" rel="noreferrer">
                    Preview
                  </a>
                )}
                <button
                  className="btn primary sm"
                  disabled={a.busy}
                  onClick={() => window.confirm(draft.requires_sign ? 'Publish it? Everyone assigned is sent a message to read and sign it.' : 'Publish this fix? Signatures on the last version still count.') && a.run(async () => (await api.post(`/api/policies/${id}/publish`, { versionId: draft.id }), r.reload()))}
                >
                  Publish version {draft.version}
                </button>
              </div>
            )}
            <VersionUpload id={id} onDone={r.reload} />
          </Card>
          <Card
            title={`Who has signed${r.data.signVersion ? ` version ${r.data.signVersion.version}` : ''} · ${people.length - unsigned.length} of ${people.length}`}
            actions={
              <a className="btn sm" href={`/api/policies/${id}/export`}>
                Export (Excel)
              </a>
            }
          >
            {people.length === 0 ? (
              <p className="muted small">No one is assigned yet.</p>
            ) : (
              <>
                <div className="seg-ctl" role="tablist" aria-label="Who">
                  <button type="button" role="tab" aria-selected={!showAll} className={!showAll ? 'on' : ''} onClick={() => setShowAll(false)}>
                    Not signed ({unsigned.length})
                  </button>
                  <button type="button" role="tab" aria-selected={showAll} className={showAll ? 'on' : ''} onClick={() => setShowAll(true)}>
                    Everyone
                  </button>
                </div>
                <table className="table compact">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Unit</th>
                      <th>Signed</th>
                      <th>Last opened</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showAll ? people : unsigned).map((x: any) => (
                      <tr key={x.id}>
                        <td>{x.name}</td>
                        <td>{x.unit ?? '—'}</td>
                        <td>{x.signed_at ? `${when(x.signed_at)}${x.method === 'imported' ? ` (imported${x.source ? `, ${x.source}` : ''})` : ''}` : <Badge tone="warn">Not signed</Badge>}</td>
                        <td>{x.last_opened ? when(x.last_opened) : <span className="muted">Never</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </Card>
          <Card title="Versions">
            <ul className="plain pol-versions">
              {versions.map((v: any) => (
                <li key={v.id}>
                  <strong>Version {v.version}</strong> <Badge tone={v.status === 'published' ? 'ok' : v.status === 'draft' ? 'warn' : 'neutral'}>{v.status === 'superseded' ? 'Replaced' : v.status === 'draft' ? 'Draft' : 'In effect'}</Badge>{' '}
                  <span className="small muted">
                    {v.published_at ? `published ${when(v.published_at)} by ${v.published_by_name}` : `saved ${when(v.created_at)}`}
                    {v.change_note ? ` · ${v.change_note}` : ''}
                  </span>{' '}
                  {v.file_name && (
                    <a className="small" href={`/api/policies/versions/${v.id}/file`} target="_blank" rel="noreferrer">
                      PDF
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        </>
      )}
    </div>
  );
}

export function PolicyImport() {
  const { navigate } = useRouter();
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [source, setSource] = useState('PowerDMS');
  const [createMissing, setCreateMissing] = useState(true);
  const [res, setRes] = useState<any>(null);
  const a = useAction();
  const run = (preview: boolean) => a.run(async () => setRes(await api.post('/api/policies/import', { csv, source, createMissing, preview })));
  return (
    <div className="ops stack">
      <PageHeader crumb="Policies" title="Import signatures" sub="Bring in who signed what from the old system (PowerDMS or a spreadsheet), so it all counts here." />
      <Card title="1. The export">
        <p className="small">
          Export the signature report from PowerDMS (or any spreadsheet) as a <strong>CSV</strong>. It needs columns for the <strong>policy</strong> (number or title), the <strong>person</strong> (name, username, email or badge #) and the <strong>date signed</strong>. Column names are recognized on their own.
        </p>
        <label>
          CSV file
          <input
            type="file"
            accept=".csv,text/csv,.txt"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setFileName(f.name);
              setCsv(await f.text());
              setRes(null);
            }}
          />
        </label>
        {fileName && <p className="small muted">{fileName} · {csv.split(/\r?\n/).filter(Boolean).length - 1} rows</p>}
        <div className="grid-3">
          <label>
            Came from
            <input value={source} onChange={(e) => setSource(e.target.value)} />
          </label>
          <label className="check" style={{ alignSelf: 'end' }}>
            <input type="checkbox" checked={createMissing} onChange={(e) => setCreateMissing(e.target.checked)} /> Add policies that aren’t in BlueHQ yet
          </label>
        </div>
        <ErrorBox error={a.error} />
        <div className="row gap-sm">
          <button className="btn" disabled={!csv || a.busy} onClick={() => run(true)}>
            Check it first
          </button>
          <button className="btn primary" disabled={!csv || a.busy || !res?.preview} onClick={() => window.confirm(`Import ${res.imported} signatures?`) && run(false)}>
            Import
          </button>
        </div>
      </Card>
      {res && (
        <Card title={res.preview ? '2. What will happen' : 'Done'}>
          <ul className="plain">
            <li>
              <strong>{res.imported}</strong> signature{res.imported === 1 ? '' : 's'} {res.preview ? 'will be imported' : 'imported'}
            </li>
            {res.duplicates > 0 && <li>{res.duplicates} already in BlueHQ (skipped)</li>}
            {res.created.length > 0 && (
              <li>
                {res.preview ? 'Policies that will be added' : 'Policies added'} ({res.created.length}): {res.created.join('; ')}
                {!res.preview && <div className="small muted">Open each one to upload its document.</div>}
              </li>
            )}
            {res.unmatchedCount > 0 && <li className="warn-text">{res.unmatchedCount} row{res.unmatchedCount === 1 ? '' : 's'} couldn’t be matched (below)</li>}
          </ul>
          {res.unmatched.length > 0 && (
            <table className="table compact">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Why</th>
                  <th>What it says</th>
                </tr>
              </thead>
              <tbody>
                {res.unmatched.map((u: any) => (
                  <tr key={u.row}>
                    <td>{u.row}</td>
                    <td>{u.reason}</td>
                    <td className="small">{u.text}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!res.preview && (
            <button className="btn" onClick={() => navigate(at('/policies'))}>
              Back to policies
            </button>
          )}
        </Card>
      )}
    </div>
  );
}
